import { html, css } from 'lit';

import '../../../../packages/neb-lit-components/src/components/tables/neb-table';
import Overlay from '../../../../packages/neb-lit-components/src/components/overlays/neb-overlay';
import {
  OVERLAY_KEYS,
  openOverlay,
} from '../../../../packages/neb-lit-components/src/utils/overlay-constants';
import { parseDate } from '../../../../packages/neb-utils/date-util';
import { MODE } from '../../../../packages/neb-utils/table';
import '../../../../packages/neb-lit-components/src/components/neb-popup-header';
import { CSS_SPACING, CSS_COLOR_GREY_1 } from '../../../styles';
import {
  getDataIntegrityQueryList,
  getDataIntegrityJobQueries,
} from '../api-clients/data-integrity';
import { mapToJobGroups } from '../mappers/job-group-map';
import { mapToJobQueries } from '../mappers/job-query-map';
import { formatMsToMinSec } from '../utils/date-util';
import '../../../../packages/neb-lit-components/src/components/inputs/neb-select';

const DATE_TIME_FORMAT = 'YYYY-MM-DD   hh:mm:ss A';

export const ELEMENTS = {
  header: {
    id: 'header',
    title: 'Data Integrity Job Queries',
  },
  jobQueriesTable: { id: 'job-queries-table' },
  jobGroupDropdown: { id: 'job-group-dropdown' },
};

const getTableConfig = () => [
  {
    key: 'jiraId',
    label: 'Issue',
    flex: css`0 0 80px`,
  },
  {
    key: 'description',
    label: 'Description',
    flex: css`1 0 0`,
  },
  {
    key: 'tenantsCount',
    label: 'Tenants checked',
    flex: css`0 0 100px`,
  },
  {
    key: 'tenantsAffected.tenantsCount',
    label: 'Tenants affected',
    flex: css`0 0 100px`,
  },
  {
    key: 'tenantsAffected.recordsCount',
    label: 'Records affected',
    flex: css`0 0 100px`,
  },
  {
    key: 'executionTimeMs',
    label: 'Execution Time (minutes)',
    flex: css`0 0 100px`,
    formatter: value => formatMsToMinSec(value),
  },
  {
    key: 'spacer',
    label: '',
    flex: css`0 0 ${CSS_SPACING}`,
  },
];

class NebOverlayDataIntegrityJobQueries extends Overlay {
  static get properties() {
    return {
      __jobGroups: Array,
      __jobQueries: Array,
      __selectedJobGroup: String,
      __items: Array,
    };
  }

  constructor() {
    super();

    this.__initState();
    this.__initHandlers();
  }

  __initState() {
    this.__jobQueries = [];
    this.__jobGroups = [];
    this.__selectedJobGroup = '';
    this.model = {
      jobId: '',
      executionTimeMs: '',
      endedAt: '',
    };

    this.__items = [];
  }

  __initHandlers() {
    this.__handlers = {
      changeJobGroup: e => {
        this.__selectedJobGroup = e.value;
        this.__loadTableItems();
      },
      openQueryJobsOverlay: (_, item) => {
        this.__openQueryJobsOverlay(item);
      },
    };
  }

  async __openQueryJobsOverlay(item) {
    await openOverlay(OVERLAY_KEYS.DATA_INTEGRITY_QUERY_JOBS, {
      queryId: item.id,
      issueId: item.jiraId,
      description: item.description,
    });
  }

  __getJobDescriptionLabel() {
    const lastRun = this.model.endedAt
      ? parseDate(this.model.endedAt).format(DATE_TIME_FORMAT)
      : '';
    const executionTime = this.model.executionTimeMs
      ? formatMsToMinSec(this.model.executionTimeMs)
      : '';

    return html`Job ID: ${this.model.jobId} || Last run: ${lastRun} || Execution
    time (minutes): ${executionTime}`;
  }

  __formatJobGroupsToItems(jobGroups) {
    const jobGroupsFormatted = jobGroups.map(data => ({
      label: data.description,
      data,
    }));

    return jobGroupsFormatted;
  }

  async __loadData(jobId) {
    const [jobQueries, queryList] = await Promise.all([
      getDataIntegrityJobQueries(jobId),
      getDataIntegrityQueryList(),
    ]);
    this.__jobQueries = mapToJobQueries({ queries: jobQueries, queryList });
    this.__jobGroups = this.__formatJobGroupsToItems(mapToJobGroups(queryList));
    this.__selectedJobGroup =
      this.__jobGroups.length > 0 ? this.__jobGroups[0] : '';

    this.__loadTableItems();
  }

  __loadTableItems() {
    this.__items = this.__jobQueries.filter(
      query => query.groupId === this.__selectedJobGroup.data.id,
    );
  }

  async connectedCallback() {
    super.connectedCallback();

    if (this.model.jobId) {
      await this.__loadData(this.model.jobId);
    }
  }

  static get styles() {
    return [
      super.styles,
      css`
        .header {
          padding: ${CSS_SPACING};
        }

        .content {
          display: flex;
          width: 100%;
          flex-flow: column nowrap;
        }

        .main-content {
          display: flex;
          flex-direction: column;
          flex: 1 0 0;
          overflow: auto;
        }
        .text {
          color: ${CSS_COLOR_GREY_1};
          font-style: italic;
          margin: ${CSS_SPACING};
          flex: 1;
        }
        .dropdown {
          display: flex;
          margin: ${CSS_SPACING};
          width: 300px;
        }
        .controls-row {
          display: flex;
          align-items: center;
        }
      `,
    ];
  }

  renderContent() {
    return html`
      <neb-popup-header
        id="${ELEMENTS.header.id}"
        class="header"
        title="${ELEMENTS.header.title}"
        .onCancel="${this.handlers.dismiss}"
        showCancelButton
      ></neb-popup-header>
      <div class="controls-row">
        <neb-select
          id="${ELEMENTS.jobGroupDropdown.id}"
          class="dropdown"
          label="Job Group"
          name="id"
          .items="${this.__jobGroups}"
          .value="${this.__selectedJobGroup}"
          .onChange="${this.__handlers.changeJobGroup}"
        ></neb-select>

        <div class="text">${this.__getJobDescriptionLabel()}</div>
      </div>
      <div class="main-content">
        <neb-table
          id="${ELEMENTS.jobQueriesTable.id}"
          .config="${getTableConfig()}"
          .model="${this.__items}"
          .mode="${MODE.DETAIL}"
          showShadedRows
          .onSelectRow="${this.__handlers.openQueryJobsOverlay}"
        ></neb-table>
      </div>
    `;
  }
}

customElements.define(
  'neb-overlay-data-integrity-job-queries',
  NebOverlayDataIntegrityJobQueries,
);
